import type { SavedDeck } from '../hooks/useSavedDecks'
import { tc } from '../i18n'

/**
 * The one place a deck's display title is decided. Every surface that renders a
 * deck name uses this so the Decks list, deck detail, shared view, and public
 * profile cannot drift apart.
 */
export function deckTitle(deck: Pick<SavedDeck, 'name' | 'commander'>): string {
  const named = deck.name?.trim()
  if (named) return named
  const commander = deck.commander?.trim()
  if (commander) return commander
  return tc('untitled_deck')
}

/**
 * Stable digest of a decklist's CARD CONTENT, used to decide whether a linked
 * deck was edited in Commander Bracket since its last pull.
 *
 * Deliberately order-insensitive and blank-line-insensitive: a re-fetch that
 * returns the same cards in a different order is NOT an edit. This is a change
 * detector, not a security primitive, so a cheap non-crypto hash is correct -
 * it keeps the function synchronous (SubtleCrypto is async and would force
 * every caller to await).
 */
export function hashDecklist(decklist: string): string {
  const lines = decklist
    .split('\n')
    .map(l => l.trim())
    .filter(l => l.length > 0 && !l.startsWith('//'))
    .sort()

  const normalized = lines.join('\n')

  // FNV-1a, 32-bit.
  let hash = 0x811c9dc5
  for (let i = 0; i < normalized.length; i++) {
    hash ^= normalized.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}
